/** The compact path to filing: five small dots and the name of the station the filing is at, in words.
 *
 * It is the filing page's path (`PathToFiling`) at table size, for a row of the Filings list and the
 * Home strip, where the full chevrons were cut to "D.. F.. A. D. I..". A row is read by the one
 * station that matters and a count, not by five labels: "Data in, blocked · 2 of 5 done". The other
 * stations are in the tooltip and in the list a screen reader hears.
 *
 * **The current station is the first one that is not done** (`stepsFromChecks` marks it), so a later
 * advisory note never takes the place of an earlier blocker. **State is a shape and a word, never
 * colour alone**: each dot is a different shape per state (design rule 4), and every dot has its
 * state in text. The steps are statements the server made; this only draws them, decides nothing and
 * counts only the stations it is given.
 */

import { STATE_WORD, stepperText } from "../../screens/filingSpine";
import type { SpineStep } from "../../screens/filingSpine";
import styles from "./FilingStepper.module.css";

export function FilingStepper({
  steps,
  label,
  testId,
}: {
  steps: readonly SpineStep[];
  /** What the list is, for a screen reader: "Path, Vallecito Industries". */
  label: string;
  testId?: string;
}) {
  const text = stepperText(steps);
  const all = steps
    .map((step) => `${step.label}: ${STATE_WORD[step.state]}${step.note ? `, ${step.note}` : ""}`)
    .join("; ");
  return (
    <span className={styles.stepper} title={all} data-testid={testId ?? "filing-stepper"}>
      <ol className={styles.dots} aria-label={label}>
        {steps.map((step) => (
          <li
            key={step.key}
            className={styles.dot}
            data-state={step.state}
            data-here={step.here ? "" : undefined}
          >
            <span className="visuallyHidden">
              {step.label}: {STATE_WORD[step.state]}
            </span>
          </li>
        ))}
      </ol>
      <span className={styles.text} data-testid="stepper-text">
        {text}
      </span>
    </span>
  );
}
